/**
 * ShipNowModal — v4
 * ==================
 * Tab-based fulfillment modal:
 *
 * [ Marketplace Shipping ] — 4-step wizard
 *   Step 1  Shipment Details — full customer address (read-only) + per-box weight/dims/VolumetricBudgetMeter
 *   Step 2  Shipping Options — all carriers visible (Delhivery + Shiprocket), skeletons while loading
 *   Step 3  Review & Confirm
 *   Step 4  Shipment Created
 *
 * [ Self Managed ] — Dispatch form → Mark as Dispatched
 */

import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input }  from '../ui/input';
import { Label }  from '../ui/label';
import { Badge }  from '../ui/badge';
import { toast }  from '../ui/sonner';
import VolumetricBudgetMeter from '../VolumetricBudgetMeter';
import {
  Truck, Zap, Package, CheckCircle2, ExternalLink, ChevronDown,
  Loader2, AlertCircle, Plus, X, Info, User, Car, ArrowRight, Scale,
} from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const WEIGHT_UNITS = [
  { value: 'g',   label: 'g',   toKg: v => v / 1000 },
  { value: 'kg',  label: 'kg',  toKg: v => v },
  { value: 'lbs', label: 'lbs', toKg: v => v * 0.453592 },
];

const DEFAULT_BOX      = { weight_kg: 0.5, length: 15, width: 12, height: 10 };
const HL_MAX_WEIGHT_KG = 15;
const HL_MAX_L = 44; const HL_MAX_W = 42; const HL_MAX_H = 40;
const BACKEND_URL      = process.env.REACT_APP_BACKEND_URL;

function pad2(n) { return String(n).padStart(2, '0'); }
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// ─── Stepper (DelhiveryShipping style) ────────────────────────────────────────

function Stepper({ currentStep, steps }) {
  return (
    <div className="flex items-center justify-center gap-0 px-4 py-2">
      {steps.map((label, i) => {
        const stepNum   = i + 1;
        const isActive  = stepNum === currentStep;
        const isDone    = stepNum < currentStep;
        const isLast    = i === steps.length - 1;
        return (
          <div key={i} className="flex items-center">
            {/* circle */}
            <div className="flex flex-col items-center">
              <div className={[
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all',
                isDone  ? 'bg-blue-600 border-blue-600 text-white'
                : isActive ? 'bg-white border-blue-600 text-blue-600'
                : 'bg-white border-gray-300 text-gray-400',
              ].join(' ')}>
                {isDone ? <CheckCircle2 className="h-4 w-4" /> : stepNum}
              </div>
              <span className={`mt-1 text-[10px] font-medium hidden sm:block ${isActive ? 'text-blue-600' : isDone ? 'text-blue-500' : 'text-gray-400'}`}>
                {label}
              </span>
            </div>
            {/* connector */}
            {!isLast && (
              <div className={`h-0.5 w-10 sm:w-16 mx-1 transition-colors ${isDone ? 'bg-blue-600' : 'bg-gray-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ShipNowModal({ order, open, onClose, onShipped }) {
  const { apiCall } = useAuth();

  const [activeTab, setActiveTab] = useState('marketplace');

  // Marketplace
  const [boxes,          setBoxes]         = useState([{ ...DEFAULT_BOX }]);
  const [weightUnit,     setWeightUnit]     = useState('g');
  const [mpStep,         setMpStep]         = useState(1);
  const [analyzing,      setAnalyzing]      = useState(false);
  const [analyzeResult,  setAnalyzeResult]  = useState(null);
  const [analyzeError,   setAnalyzeError]   = useState(null);
  const [billingSlabKg,  setBillingSlabKg]  = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isCod,          setIsCod]          = useState((order?.payment_mode || '').toLowerCase() === 'cod');
  const [creating,       setCreating]       = useState(false);
  const [shipResult,     setShipResult]     = useState(null);

  const [showMoreSR,     setShowMoreSR]   = useState(false);

  // Self Managed
  const [selfManaged, setSelfManaged] = useState({
    courier_name: '', awb_number: '', dispatch_date: todayStr(),
    tracking_url: '', shipping_cost: '', notes: '',
  });
  const [smCreating, setSmCreating] = useState(false);
  const [smDone,     setSmDone]     = useState(false);

  // ── Derived ───────────────────────────────────────────────────────────────────

  const unitConverter = WEIGHT_UNITS.find(u => u.value === weightUnit) || WEIGHT_UNITS[1];
  const totalWeightKg = parseFloat(
    boxes.reduce((s, b) => s + parseFloat(b.weight_kg || 0), 0).toFixed(3)
  );
  const isMultiBox = boxes.length > 1;
  const hlBlocked  = isMultiBox
    || totalWeightKg > HL_MAX_WEIGHT_KG
    || boxes.some(b =>
        parseFloat(b.length) > HL_MAX_L || parseFloat(b.width) > HL_MAX_W || parseFloat(b.height) > HL_MAX_H
      );

  const shopifyShipLine  = order?.shipping_lines?.[0];
  const shopifyShipTitle = (shopifyShipLine?.title || '').toLowerCase();
  const customerWantsExpress  = shopifyShipTitle.includes('express') || shopifyShipTitle.includes('air') ||
                                 shopifyShipTitle.includes('priority') || shopifyShipTitle.includes('fast');
  const customerWantsSameDay  = shopifyShipTitle.includes('same day') || shopifyShipTitle.includes('same-day') ||
                                 shopifyShipTitle.includes('sameday') || shopifyShipTitle.includes('quick') ||
                                 shopifyShipTitle.includes('hyperlocal') || shopifyShipTitle.includes('instant');

  const weightDisplayValue = (() => {
    const kg = boxes[0]?.weight_kg || 0.5;
    if (weightUnit === 'g')   return parseFloat((kg * 1000).toFixed(0));
    if (weightUnit === 'lbs') return parseFloat((kg / 0.453592).toFixed(2));
    return parseFloat(kg.toFixed(3));
  })();

  // ── Address helpers ───────────────────────────────────────────────────────────

  const addr = order?.shipping_address || order?.customer_address || {};
  const recipientName  = addr.name  || order?.customer?.name  || order?.customer_name  || '';
  const recipientPhone = addr.phone || order?.customer?.phone || order?.customer_phone || '';
  const recipientEmail = addr.email || order?.customer?.email || '';
  const recipientAddr  = [addr.address1 || addr.address, addr.address2].filter(Boolean).join(', ');
  const recipientCity  = addr.city     || '';
  const recipientState = addr.province || addr.state || '';
  const recipientPin   = addr.zip      || addr.pincode || '';

  // ── Box helpers ───────────────────────────────────────────────────────────────

  function addBox()             { setBoxes(p => [...p, { ...DEFAULT_BOX }]); }
  function removeBox(idx)       { if (boxes.length > 1) setBoxes(p => p.filter((_, i) => i !== idx)); }
  function updateBox(idx, f, v) { setBoxes(p => p.map((b, i) => i === idx ? { ...b, [f]: v } : b)); }

  function handleWeightInput(raw) {
    const numVal = parseFloat(raw) || 0;
    const kg = Math.max(unitConverter.toKg(numVal), 0.001);
    updateBox(0, 'weight_kg', parseFloat(kg.toFixed(4)));
  }

  // ── Reset ─────────────────────────────────────────────────────────────────────

  function resetModal() {
    setActiveTab('marketplace');
    setMpStep(1);
    setBoxes([{ ...DEFAULT_BOX }]);
    setWeightUnit('g');
    setBillingSlabKg(null);
    setAnalyzeResult(null);
    setAnalyzeError(null);
    setSelectedOption(null);
    setShipResult(null);
    setShowMoreSR(false);
    setSelfManaged({ courier_name: '', awb_number: '', dispatch_date: todayStr(), tracking_url: '', shipping_cost: '', notes: '' });
    setSmDone(false);
  }
  function handleClose() { resetModal(); onClose(); }

  // ── Marketplace: Step 1 → 2 ──────────────────────────────────────────────────

  async function goFetchOptions() {
    setAnalyzing(true);
    setAnalyzeError(null);
    setAnalyzeResult(null);
    setSelectedOption(null);
    setMpStep(2);  // show skeleton immediately

    try {
      const firstBox = boxes[0];
      let chargeableKg = totalWeightKg;
      try {
        const pvResp = await fetch(`${BACKEND_URL}/api/volumetric/preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            length_cm: Number(firstBox.length), width_cm: Number(firstBox.width),
            height_cm: Number(firstBox.height), weight_kg: totalWeightKg,
          }),
        });
        if (pvResp.ok) {
          const pv = await pvResp.json();
          chargeableKg = pv.weight?.billing_slab_kg || totalWeightKg;
          setBillingSlabKg(chargeableKg);
        }
      } catch (_) { /* non-blocking */ }

      const data = await apiCall('/api/shipping/analyze', {
        method: 'POST',
        body: JSON.stringify({
          order_id:             order.id,
          weight_kg:            totalWeightKg,
          chargeable_weight_kg: chargeableKg,
          length:               Number(firstBox.length),
          width:                Number(firstBox.width),
          height:               Number(firstBox.height),
          box_count:            boxes.length,
        }),
      });
      setAnalyzeResult(data);
      autoSelect(data);
    } catch (e) {
      setAnalyzeError(e?.detail?.message || e?.message || 'Failed to fetch shipping options');
    } finally {
      setAnalyzing(false);
    }
  }

  function autoSelect(data) {
    if (!data?.tiers) return;
    const tiers = data.tiers;
    // If customer chose Same-Day on storefront, pre-select Quick if eligible
    if (customerWantsSameDay && !hlBlocked && tiers.same_day?.options?.[0]) {
      setSelectedOption(tiers.same_day.options[0]); return;
    }
    // Otherwise default to Delhivery — prefer surface/air based on customer checkout selection
    const preferred = customerWantsExpress ? 'express' : 'standard';
    const fallback  = customerWantsExpress ? 'standard' : 'express';
    const prefDelhivery = (tiers[preferred]?.options || []).find(o => o.provider === 'Delhivery');
    if (prefDelhivery) { setSelectedOption(prefDelhivery); return; }
    const fbDelhivery = (tiers[fallback]?.options || []).find(o => o.provider === 'Delhivery');
    if (fbDelhivery) { setSelectedOption(fbDelhivery); return; }
    // Fallback: any standard/express option
    const anyOpt = (tiers[preferred]?.options || [])[0] || (tiers[fallback]?.options || [])[0];
    if (anyOpt) { setSelectedOption(anyOpt); return; }
    // Last resort: heavy intercity or quick
    const heavy = (tiers.heavy_intercity?.options || [])[0];
    if (heavy) { setSelectedOption(heavy); return; }
    const quick = tiers.same_day?.options?.[0];
    if (quick && !hlBlocked) setSelectedOption(quick);
  }

  // ── Marketplace: create shipment ──────────────────────────────────────────────

  async function createShipment() {
    if (!selectedOption) { toast.error('Please select a carrier first'); return; }
    setCreating(true);
    try {
      const isQuick = selectedOption.provider === 'Shiprocket Quick';
      const isSR    = selectedOption.provider?.startsWith('Shiprocket');
      const endpoint = isSR ? '/api/shiprocket/shipments' : '/api/delhivery/shipments';

      const body = {
        order_id:            order.id,
        weight:              totalWeightKg,
        length:              Math.max(...boxes.map(b => parseFloat(b.length))),
        width:               Math.max(...boxes.map(b => parseFloat(b.width))),
        height:              Math.max(...boxes.map(b => parseFloat(b.height))),
        consignee_name:      recipientName   || 'Customer',
        consignee_phone:     recipientPhone  || '0000000000',
        consignee_address:   recipientAddr,
        consignee_city:      recipientCity,
        consignee_state:     recipientState,
        consignee_pincode:   recipientPin,
        pickup_location:     '',
        product_description: '',
        payment_mode:        isCod ? 'COD' : 'Prepaid',
        order_value:         parseFloat(order.pricing?.total || order.total_price || 0),
        ...(isQuick ? { use_quick: true } : {}),
        ...(isSR && selectedOption.courier_id ? { courier_id: selectedOption.courier_id } : {}),
        ...(isMultiBox ? {
          boxes: boxes.map(b => ({
            weight_kg: parseFloat(b.weight_kg), length: parseFloat(b.length),
            width: parseFloat(b.width), height: parseFloat(b.height),
          })),
        } : {}),
      };

      const result = await apiCall(endpoint, { method: 'POST', body: JSON.stringify(body) });
      setShipResult(result);
      setMpStep(4);
      toast.success(`Shipment created — AWB: ${result.awb || result.awb_number}`);
      onShipped?.(result);
    } catch (e) {
      const detail    = e?.detail || {};
      const errType   = detail?.carrier_error_type || '';
      const rawMsg    = detail?.carrier_raw_message;
      const fallback  = detail?.message || e?.message || 'Shipment creation failed';
      let title = 'Shipment Failed', explanation = '';
      if (errType === 'embargo') {
        title = 'Delivery temporarily unavailable';
        explanation = 'Delhivery has placed a temporary embargo on this route.';
      } else if (errType === 'customer_pincode_not_serviceable') {
        title = 'Delivery address not serviceable';
        explanation = `Pincode ${detail?.pincode || ''} is not in the carrier's network.`;
      } else if (errType === 'stale_warehouse') {
        title = 'Pickup registration needs refresh';
        explanation = 'Go to Pack & Ship → Pickup Locations and click "Re-register".';
      } else {
        explanation = fallback;
      }
      toast.error(title, {
        description: `${explanation}${rawMsg ? `\nCarrier: ${rawMsg}` : ''}`,
        duration: 8000,
      });
    } finally {
      setCreating(false);
    }
  }

  // ── Self Managed ──────────────────────────────────────────────────────────────

  async function markDispatched() {
    if (!selfManaged.courier_name.trim()) {
      toast.error('Courier / delivery partner name is required'); return;
    }
    setSmCreating(true);
    try {
      await apiCall('/api/shipping/vendor/manual-awb', {
        method: 'POST',
        body: JSON.stringify({
          order_id:      order.id,
          courier_name:  selfManaged.courier_name.trim(),
          awb_number:    selfManaged.awb_number.trim() || `MANUAL-${Date.now()}`,
          tracking_url:  selfManaged.tracking_url.trim() || undefined,
          shipping_cost: selfManaged.shipping_cost ? parseFloat(selfManaged.shipping_cost) : undefined,
        }),
      });
      setSmDone(true);
      toast.success('Order marked as dispatched');
      onShipped?.({ provider: 'self_managed', courier: selfManaged.courier_name });
    } catch (e) {
      toast.error(e?.detail?.message || e?.message || 'Failed to mark as dispatched');
    } finally {
      setSmCreating(false);
    }
  }

  // ─── Render helpers ───────────────────────────────────────────────────────────

  // ─ Per-box card (weight + dims + VolumetricBudgetMeter inside) ─
  function renderBoxCard(box, idx) {
    const isPrimary   = idx === 0;
    const wDisplay    = (() => {
      if (!isPrimary) return null;
      const kg = boxes[0]?.weight_kg || 0.5;
      if (weightUnit === 'g')   return parseFloat((kg * 1000).toFixed(0));
      if (weightUnit === 'lbs') return parseFloat((kg / 0.453592).toFixed(2));
      return parseFloat(kg.toFixed(3));
    })();
    const dimOk = Number(box.length) > 0 && Number(box.width) > 0 && Number(box.height) > 0;

    return (
      <div key={idx} className="rounded-xl border border-gray-200 bg-white p-4 space-y-3" data-testid={`box-${idx}`}>
        {/* Box header */}
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">Box {idx + 1}</span>
          {boxes.length > 1 && (
            <button type="button" onClick={() => removeBox(idx)}
              className="text-red-400 hover:text-red-600 transition-colors" data-testid={`remove-box-${idx}`}>
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Weight row — full unit selector on primary box only */}
        <div data-testid={`box-${idx}-weight-row`}>
          <Label className="text-xs font-medium mb-1.5 block">Weight</Label>
          {isPrimary ? (
            <>
              <div className="flex gap-2">
                <Input
                  type="number" step="0.01" min="0.01"
                  value={wDisplay}
                  onChange={e => handleWeightInput(e.target.value)}
                  className="flex-1 h-10 text-sm"
                  data-testid="box-0-weight"
                />
                <select
                  value={weightUnit}
                  onChange={e => setWeightUnit(e.target.value)}
                  className="w-14 rounded-md border border-input bg-background px-2 text-sm"
                  data-testid="weight-unit-select"
                >
                  {WEIGHT_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </div>
              <p className="text-xs text-gray-400 mt-1">{totalWeightKg.toFixed(3)} kg</p>
            </>
          ) : (
            <Input type="number" step="0.01" min="0.01" placeholder="kg"
              value={box.weight_kg}
              onChange={e => updateBox(idx, 'weight_kg', e.target.value)}
              className="h-9 text-sm" data-testid={`box-${idx}-weight`} />
          )}
        </div>

        {/* Dimensions */}
        <div>
          <div className="grid grid-cols-3 gap-2" data-testid={`box-${idx}-dims`}>
            {[['length','L (cm)'],['width','W (cm)'],['height','H (cm)']].map(([f, lb]) => (
              <div key={f}>
                <Label className="text-xs">{lb}</Label>
                <Input type="number" step="1" min="1"
                  value={box[f]}
                  onChange={e => updateBox(idx, f, e.target.value)}
                  className="mt-1 h-9 text-sm" data-testid={`box-${idx}-${f}`} />
              </div>
            ))}
          </div>
        </div>

        {/* VolumetricBudgetMeter — inside each box card */}
        {dimOk && (
          <VolumetricBudgetMeter
            length={box.length}
            width={box.width}
            height={box.height}
            weight={isPrimary ? totalWeightKg : parseFloat(box.weight_kg || 0)}
            mode="fulfilment"
            defaultExpanded={false}
            dataTestIdPrefix={`shipnow-box${idx}-volumetric`}
          />
        )}
      </div>
    );
  }

  // ─ Carrier card ─
  function renderCarrierCard(opt) {
    const isSelected  = selectedOption?.service_code === opt.service_code;
    const isHLQuick   = opt.provider === 'Shiprocket Quick';
    const isDelhivery = opt.provider === 'Delhivery';
    const isSR        = opt.provider === 'Shiprocket' || opt.provider === 'Shiprocket B2B';

    const isRecommended = isDelhivery && (
      (customerWantsExpress && opt.service_code?.includes('air')) ||
      (!customerWantsExpress && opt.service_code?.includes('surface'))
    );

    return (
      <button
        key={opt.service_code}
        type="button"
        onClick={() => setSelectedOption(opt)}
        data-testid={`carrier-option-${opt.service_code}`}
        className={[
          'w-full text-left rounded-xl border-2 px-4 py-3 transition-all duration-150',
          isSelected
            ? 'border-slate-800 bg-slate-800 text-white shadow-md'
            : isHLQuick
              ? 'border-green-400 bg-green-50 hover:border-green-500'
              : isDelhivery
                ? 'border-blue-300 bg-blue-50 hover:border-blue-500'
                : isSR
                  ? 'border-indigo-200 bg-indigo-50 hover:border-indigo-400'
                  : 'border-gray-200 bg-white hover:border-gray-400',
        ].join(' ')}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            {/* radio indicator */}
            <div className={[
              'w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center',
              isSelected    ? 'border-white'
                : isHLQuick  ? 'border-green-500'
                : isDelhivery ? 'border-blue-500'
                : isSR        ? 'border-indigo-500'
                : 'border-gray-400',
            ].join(' ')}>
              {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={`font-semibold text-sm ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                  {opt.service_name}
                </span>
                {isHLQuick && (
                  <Badge className="text-[10px] bg-green-200 text-green-800 border-0 h-4 px-1.5">
                    <Zap className="h-2.5 w-2.5 mr-0.5" />Quick
                  </Badge>
                )}
                {isRecommended && (
                  <Badge className={`text-[10px] border-0 h-4 px-1.5 ${isSelected ? 'bg-white/25 text-white' : 'bg-blue-100 text-blue-700'}`}>
                    Recommended
                  </Badge>
                )}
              </div>
              <p className={`text-xs mt-0.5 ${isSelected ? 'text-slate-300' : isHLQuick ? 'text-green-700' : isDelhivery ? 'text-blue-600' : isSR ? 'text-indigo-600' : 'text-gray-500'}`}>
                {opt.etd_label} · {opt.provider}
                {opt.min_weight_kg > 0 ? ` · min ${opt.min_weight_kg} kg` : ''}
              </p>
            </div>
          </div>
          <span className={`font-bold text-base shrink-0 ml-3 ${isSelected ? 'text-white' : 'text-gray-900'}`}>
            ₹{Math.round(opt.total_inr)}
          </span>
        </div>
      </button>
    );
  }

  // ─ Skeleton ─
  function renderSkeleton() {
    return (
      <div className="space-y-3 py-2" data-testid="shipping-options-skeleton">
        <p className="text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-blue-500" /> Fetching live rates…
        </p>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="rounded-xl border-2 border-gray-100 px-4 py-3 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-4 h-4 rounded-full bg-gray-200" />
                <div>
                  <div className="h-3 w-36 bg-gray-200 rounded mb-2" />
                  <div className="h-2.5 w-24 bg-gray-100 rounded" />
                </div>
              </div>
              <div className="h-5 w-10 bg-gray-200 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ─── MARKETPLACE STEP RENDERERS ───────────────────────────────────────────────

  function renderMpStep1() {
    return (
      <div className="space-y-4" data-testid="shipnow-step1">

        {/* Customer / Recipient Details — full address, read-only */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-800">Recipient Details</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Name</Label>
              <p className="text-sm font-medium mt-0.5">{recipientName || '—'}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Phone</Label>
              <p className="text-sm font-medium mt-0.5">{recipientPhone || '—'}</p>
            </div>
          </div>
          {recipientEmail && (
            <div>
              <Label className="text-xs text-gray-500">Email</Label>
              <p className="text-sm text-gray-600 mt-0.5">{recipientEmail}</p>
            </div>
          )}
          {recipientAddr && (
            <div>
              <Label className="text-xs text-gray-500">Address</Label>
              <p className="text-sm text-gray-700 mt-0.5 leading-relaxed">{recipientAddr}</p>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div>
              <Label className="text-xs text-gray-500">City</Label>
              <p className="font-medium mt-0.5">{recipientCity || '—'}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">State</Label>
              <p className="font-medium mt-0.5">{recipientState || '—'}</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Pincode</Label>
              <p className="font-medium mt-0.5 font-mono">{recipientPin || '—'}</p>
            </div>
          </div>
        </div>

        {/* Box cards — each has weight + dims + VolumetricBudgetMeter inside */}
        {boxes.map((box, idx) => renderBoxCard(box, idx))}

        {/* Add box */}
        <div className="flex items-center justify-between">
          <button type="button" onClick={addBox}
            className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
            data-testid="add-box-btn">
            <Plus className="h-4 w-4" /> Add Box
          </button>
          {isMultiBox && (
            <span className="text-xs text-gray-400">{boxes.length} boxes · {totalWeightKg.toFixed(2)} kg total</span>
          )}
        </div>

        {/* HL restriction */}
        {hlBlocked && (
          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3"
            data-testid="hyperlocal-multibox-notice">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              {isMultiBox
                ? 'Same-Day delivery unavailable for multi-box shipments.'
                : `Same-Day requires ≤${HL_MAX_WEIGHT_KG} kg and ≤${HL_MAX_L}×${HL_MAX_W}×${HL_MAX_H} cm.`}
            </span>
          </div>
        )}

        {/* Shopify customer preference */}
        {shopifyShipLine && (
          <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <Truck className="h-3.5 w-3.5 shrink-0" />
            Customer chose: <span className="font-semibold ml-1">{shopifyShipLine.title}</span>
          </div>
        )}

        <Button className="w-full h-11 text-sm" onClick={goFetchOptions} data-testid="fetch-rates-btn">
          <Package className="h-4 w-4 mr-2" />
          Get Shipping Options
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    );
  }

  function renderMpStep2() {
    if (analyzing) return renderSkeleton();

    if (analyzeError) {
      return (
        <div className="space-y-4 py-2" data-testid="shipnow-step2-error">
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
            <AlertCircle className="h-4 w-4 shrink-0" /> {analyzeError}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setMpStep(1)} className="flex-1">Back</Button>
            <Button onClick={goFetchOptions} className="flex-1">Retry</Button>
          </div>
        </div>
      );
    }

    if (!analyzeResult) return null;

    const tiers = analyzeResult.tiers || {};
    const isHyperlocal = (analyzeResult.is_hyperlocal || analyzeResult.same_city_match) && !hlBlocked;

    const quickOpts  = tiers.same_day?.options       || [];
    const stdOpts    = tiers.standard?.options        || [];
    const expOpts    = tiers.express?.options         || [];
    const heavyOpts  = tiers.heavy_intercity?.options || [];
    const b2bTier    = tiers.b2b_local || null;

    // Merge standard + express, deduplicated by service_code
    const allOpts = [
      ...stdOpts,
      ...expOpts.filter(o => !stdOpts.some(s => s.service_code === o.service_code)),
    ];

    // 1. Delhivery options — surface first, then air
    const delhiveryOpts = allOpts
      .filter(o => o.provider === 'Delhivery')
      .sort((a, b) => {
        const aIsAir = (a.service_code || '').includes('air') || (a.service_name || '').toLowerCase().includes('air');
        const bIsAir = (b.service_code || '').includes('air') || (b.service_name || '').toLowerCase().includes('air');
        return aIsAir - bIsAir; // surface (false=0) before air (true=1)
      });

    // 3. Shiprocket courier options sorted by price, top 2 + more
    const shiprocketOpts = allOpts
      .filter(o => o.provider === 'Shiprocket' || (o.provider === 'Shiprocket B2B' && !o.vehicle_type))
      .sort((a, b) => a.total_inr - b.total_inr);
    const SR_VISIBLE = 2;
    const srVisible  = showMoreSR ? shiprocketOpts : shiprocketOpts.slice(0, SR_VISIBLE);
    const hasSRMore  = shiprocketOpts.length > SR_VISIBLE;

    const hasAny = delhiveryOpts.length > 0 || quickOpts.length > 0 || shiprocketOpts.length > 0 || heavyOpts.length > 0;

    return (
      <div className="space-y-3 py-1" data-testid="shipnow-step2">
        {/* Weight/billing summary */}
        <div className="flex items-center gap-2 text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <Scale className="h-3.5 w-3.5 text-gray-400 shrink-0" />
          <span className="text-gray-600">
            Physical: <strong>{totalWeightKg.toFixed(2)} kg</strong>
            {billingSlabKg && billingSlabKg !== totalWeightKg && (
              <> · Billed as: <strong className="text-amber-700">{billingSlabKg.toFixed(2)} kg</strong></>
            )}
            {analyzeResult.distance_km > 0 && <> · <strong>{analyzeResult.distance_km} km</strong></>}
          </span>
        </div>

        {/* Same-city badge */}
        {isHyperlocal && (
          <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-300 rounded-lg px-3 py-2">
            <Zap className="h-3.5 w-3.5 shrink-0" />
            Same-city order — eligible for Same-Day delivery
            {analyzeResult.vendor_city && analyzeResult.customer_city && analyzeResult.vendor_city !== analyzeResult.customer_city
              ? ` · ${analyzeResult.vendor_city} → ${analyzeResult.customer_city}` : ''}
          </div>
        )}

        {/* Customer chose Same-Day at checkout */}
        {customerWantsSameDay && (
          <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-300 rounded-lg px-3 py-2">
            <Zap className="h-3.5 w-3.5 shrink-0" />
            Customer selected: <span className="font-semibold ml-1">{shopifyShipLine?.title}</span>
            {hlBlocked && <span className="ml-1 text-amber-700">(Same-Day unavailable — weight/size exceeded)</span>}
          </div>
        )}

        {!hasAny && !b2bTier && (
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <AlertCircle className="h-4 w-4 shrink-0" />
            No carriers available. Verify vendor pickup registration and IP whitelist.
          </div>
        )}

        {/* Per-tier diagnostic reasons — surfaced whenever a section is empty. */}
        {analyzeResult && (() => {
          const sd = analyzeResult.tiers?.same_day || {};
          const st = analyzeResult.tiers?.standard || {};
          const sdDiag = sd.diagnostics || {};
          const stDiag = st.diagnostics || {};
          const rows = [];
          // Same-Day missing?
          if (!sd.available && quickOpts.length === 0) {
            let reason;
            if (sdDiag.provider_error) {
              reason = `Shiprocket Quick API error: ${sdDiag.provider_error}`;
            } else if (sdDiag.skipped_reason) {
              reason = `Not hyperlocal-eligible — ${sdDiag.skipped_reason}`;
            } else if (sdDiag.shiprocket_configured === false) {
              reason = 'Shiprocket credentials not configured on backend';
            } else if (sdDiag.quick_ok && sdDiag.rates_returned === 0) {
              reason = 'Shiprocket returned zero Quick-eligible couriers for this route';
            } else if (sdDiag.blocked_reason) {
              reason = `Blocked: ${sdDiag.blocked_reason}`;
            } else {
              reason = 'No Same-Day option available';
            }
            rows.push({ tier: 'Same-Day (Shiprocket Quick)', reason, testid: 'diag-same-day' });
          }
          // Shiprocket Standard missing?
          if (shiprocketOpts.length === 0) {
            let reason;
            if (stDiag.shiprocket_error) {
              reason = `Shiprocket API error: ${stDiag.shiprocket_error}`;
            } else if (stDiag.shiprocket_configured === false) {
              reason = 'Shiprocket credentials not configured on backend';
            } else if ((stDiag.shiprocket_total_rates || 0) === 0) {
              reason = 'Shiprocket returned zero rates for this origin → destination';
            } else if ((stDiag.shiprocket_surface_count || 0) === 0) {
              reason = `Shiprocket returned ${stDiag.shiprocket_total_rates} rate(s) but none matched Standard/Surface tier`;
            } else {
              reason = 'No Shiprocket Standard couriers available';
            }
            rows.push({ tier: 'Shiprocket Standard', reason, testid: 'diag-shiprocket-standard' });
          }
          if (rows.length === 0) return null;
          return (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-2.5 space-y-1.5" data-testid="shipping-diagnostics">
              <div className="font-semibold text-amber-800 flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5" /> Why some carriers are missing
              </div>
              {rows.map((r, i) => (
                <div key={i} className="text-amber-900" data-testid={r.testid}>
                  <span className="font-medium">{r.tier}:</span> {r.reason}
                </div>
              ))}
              <div className="text-[10px] text-amber-700 pt-1">
                Origin {analyzeResult.origin_city || '?'} ({analyzeResult.origin_pin}) → {analyzeResult.customer_city || '?'} ({analyzeResult.dest_pin})
                {analyzeResult.distance_km != null && ` · ${Math.round(analyzeResult.distance_km)} km`}
              </div>
            </div>
          );
        })()}

        {/* ── Scrollable carrier list ── */}
        <div className="overflow-y-auto max-h-[40vh] space-y-3 pr-0.5" data-testid="carrier-list-scroll">

          {/* 1. Delhivery — default selected */}
          {delhiveryOpts.length > 0 && (
            <div className="space-y-2" data-testid="delhivery-section">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                <Truck className="h-3 w-3 text-blue-500" /> Delhivery
              </p>
              {delhiveryOpts.map(opt => renderCarrierCard(opt))}
            </div>
          )}

          {/* 2. HyperLocal — Shiprocket Quick */}
          {quickOpts.length > 0 && (
            <div className="space-y-2" data-testid="hyperlocal-section">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                <Zap className="h-3 w-3 text-green-500" /> HyperLocal
              </p>
              {quickOpts.map(opt => renderCarrierCard(opt))}
            </div>
          )}

          {/* 3. Shiprocket Couriers — top 2 + More */}
          {shiprocketOpts.length > 0 && (
            <div className="space-y-2" data-testid="shiprocket-section">
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                <Package className="h-3 w-3 text-indigo-500" /> Shiprocket
              </p>
              {srVisible.map(opt => renderCarrierCard(opt))}
              {hasSRMore && (
                <button
                  type="button"
                  onClick={() => setShowMoreSR(v => !v)}
                  data-testid="show-more-sr-btn"
                  className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                >
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${showMoreSR ? 'rotate-180' : ''}`} />
                  {showMoreSR ? 'Show less' : `+${shiprocketOpts.length - SR_VISIBLE} more options`}
                </button>
              )}
            </div>
          )}

          {/* 📦 Heavy Intercity */}
          {heavyOpts.length > 0 && (
            <div className="rounded-xl border-2 border-amber-200 overflow-hidden" data-testid="heavy-intercity-section">
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200">
                <Package className="h-3.5 w-3.5 text-amber-700 shrink-0" />
                <span className="text-xs font-bold text-amber-800">Heavy Intercity Freight</span>
                <span className="text-xs text-amber-600 ml-1">· Surface · &gt;15 kg</span>
              </div>
              <div className="p-2 space-y-2">
                {heavyOpts.map(opt => renderCarrierCard(opt))}
              </div>
            </div>
          )}

          {/* 🏗️ B2B Local Vehicles */}
          {b2bTier && (
            <div className="rounded-xl border-2 border-orange-200 overflow-hidden" data-testid="b2b-local-section">
              <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border-b border-orange-200">
                <Car className="h-3.5 w-3.5 text-orange-700 shrink-0" />
                <span className="text-xs font-bold text-orange-800">Local Freight Vehicles</span>
                <span className="text-xs text-orange-500 ml-1">· Shiprocket B2B · Intra-city</span>
              </div>
              <div className="p-3">
                {b2bTier.available && (b2bTier.vehicles || []).length > 0 ? (
                  <div className="space-y-2">
                    {(b2bTier.vehicles || []).map((v, i) => (
                      <button key={i} type="button"
                        onClick={() => setSelectedOption({
                          service_code: `b2b_${v.vehicle_type}_${i}`,
                          service_name: v.label || v.vehicle_type,
                          provider: 'Shiprocket B2B',
                          carrier: 'Shiprocket',
                          total_inr: v.rate || 0,
                          etd_label: v.etd_label || 'Same Day',
                          vehicle_type: v.vehicle_type,
                          courier_id: v.courier_id,
                          min_weight_kg: 0,
                        })}
                        data-testid={`b2b-vehicle-${v.vehicle_type}`}
                        className={[
                          'w-full text-left rounded-xl border-2 px-4 py-3 transition-all',
                          selectedOption?.vehicle_type === v.vehicle_type
                            ? 'border-slate-800 bg-slate-800 text-white'
                            : 'border-orange-300 bg-orange-50 hover:border-orange-500',
                        ].join(' ')}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-sm">{v.label || v.vehicle_type}</p>
                            <p className="text-xs text-orange-700">{v.etd_label}{v.capacity_kg ? ` · up to ${v.capacity_kg} kg` : ''}</p>
                          </div>
                          <span className="font-bold text-base">₹{Math.round(v.rate || 0)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-start gap-2 text-xs text-orange-700" data-testid="b2b-unavailable-msg">
                    <Info className="h-4 w-4 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold mb-0.5">Service not activated on this account</p>
                      <p className="text-orange-600 leading-relaxed">{b2bTier.reason}</p>
                      {b2bTier.endpoints_tried?.length > 0 && (
                        <p className="mt-1.5 text-orange-400 font-mono text-[10px] break-all">
                          Tried: {b2bTier.endpoints_tried.join(' · ')}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>{/* end scrollable carrier list */}

        <div className="flex gap-2 pt-1">
          <Button variant="outline" onClick={() => setMpStep(1)} className="flex-1">Back</Button>
          <Button onClick={() => setMpStep(3)} disabled={!selectedOption} className="flex-1 h-10"
            data-testid="review-confirm-btn">
            Review & Confirm <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  function renderMpStep3() {
    if (!selectedOption) { setMpStep(2); return null; }
    return (
      <div className="space-y-4 py-1" data-testid="shipnow-step3">
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-2.5 text-sm">
          <p className="font-semibold text-gray-800 mb-3">Shipment Summary</p>
          {[
            ['Carrier',    `${selectedOption.service_name} via ${selectedOption.provider}`],
            ['ETA',        selectedOption.etd_label],
            ['Physical',   `${totalWeightKg.toFixed(2)} kg`],
            ...(billingSlabKg && billingSlabKg !== totalWeightKg ? [['Billed as', `${billingSlabKg.toFixed(2)} kg`]] : []),
            ['Shipping',   `₹${Math.round(selectedOption.total_inr)}`],
            ['Consignee',  `${recipientName || 'Customer'} · ${recipientCity}`],
            ['Pincode',    recipientPin],
          ].filter(([, v]) => v).map(([label, value]) => (
            <div key={label} className="flex justify-between items-start">
              <span className="text-gray-500 shrink-0">{label}</span>
              <span className="font-medium text-gray-900 text-right ml-3">{value}</span>
            </div>
          ))}
        </div>

        {/* COD / Prepaid */}
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-600 font-medium">Payment:</p>
          <div className="flex gap-2">
            {['Prepaid', 'COD'].map(m => (
              <button key={m} type="button" onClick={() => setIsCod(m === 'COD')}
                data-testid={`payment-mode-${m.toLowerCase()}`}
                className={[
                  'px-3 py-1 rounded-full text-xs font-semibold border transition-colors',
                  (m === 'COD') === isCod
                    ? 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500',
                ].join(' ')}
              >{m}</button>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setMpStep(2)} className="flex-1">Back</Button>
          <Button onClick={createShipment} disabled={creating} className="flex-1 h-10"
            data-testid="confirm-ship-btn">
            {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</> : 'Create Shipment'}
          </Button>
        </div>
      </div>
    );
  }

  function renderMpStep4() {
    const awb = shipResult?.awb || shipResult?.awb_number;
    return (
      <div className="space-y-4 text-center py-2" data-testid="shipnow-step4">
        <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
        <div>
          <p className="text-lg font-semibold">Shipment Created</p>
          <p className="text-sm text-muted-foreground mt-1">
            {shipResult?.courier_name || selectedOption?.service_name} via {selectedOption?.provider}
          </p>
        </div>
        <div className="rounded-xl border bg-gray-50 p-4 text-left space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">AWB Number</span>
            <span className="font-mono font-bold text-base" data-testid="awb-number">{awb}</span>
          </div>
          {isMultiBox && (
            <div className="flex justify-between">
              <span className="text-gray-500">Boxes</span>
              <span className="font-medium">{boxes.length} boxes · {totalWeightKg.toFixed(2)} kg</span>
            </div>
          )}
          {shipResult?.shipping_cost > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">Shipping Cost</span>
              <span className="font-semibold">₹{shipResult.shipping_cost}</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {shipResult?.label_url && (
            <a href={shipResult.label_url} target="_blank" rel="noreferrer" className="flex-1">
              <Button variant="outline" className="w-full" data-testid="download-label-btn">
                <ExternalLink className="h-4 w-4 mr-2" /> Label
              </Button>
            </a>
          )}
          {shipResult?.tracking_url && (
            <a href={shipResult.tracking_url} target="_blank" rel="noreferrer" className="flex-1">
              <Button variant="outline" className="w-full" data-testid="track-shipment-btn">
                <Truck className="h-4 w-4 mr-2" /> Track
              </Button>
            </a>
          )}
        </div>
        <Button className="w-full" onClick={handleClose} data-testid="shipnow-done-btn">Done</Button>
      </div>
    );
  }

  // ─── SELF MANAGED ─────────────────────────────────────────────────────────────

  function renderSelfManaged() {
    if (smDone) {
      return (
        <div className="space-y-4 text-center py-4" data-testid="self-managed-done">
          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
          <div>
            <p className="text-lg font-semibold">Order Dispatched</p>
            <p className="text-sm text-muted-foreground mt-1">
              Via {selfManaged.courier_name}{selfManaged.awb_number ? ` · AWB: ${selfManaged.awb_number}` : ''}
            </p>
          </div>
          <Button className="w-full" onClick={handleClose} data-testid="self-managed-done-btn">Done</Button>
        </div>
      );
    }
    return (
      <div className="space-y-4 py-1" data-testid="self-managed-form">
        <p className="text-sm text-muted-foreground">Record your own courier arrangement. The order will be marked as dispatched.</p>
        <div className="space-y-3">
          <div>
            <Label className="text-sm">Courier / Delivery Partner *</Label>
            <Input value={selfManaged.courier_name}
              onChange={e => setSelfManaged(p => ({ ...p, courier_name: e.target.value }))}
              placeholder="e.g. Bluedart, DTDC, Porter, own rider"
              className="mt-1" data-testid="sm-courier-name" />
          </div>
          <div>
            <Label className="text-sm">AWB / Tracking Number</Label>
            <Input value={selfManaged.awb_number}
              onChange={e => setSelfManaged(p => ({ ...p, awb_number: e.target.value }))}
              placeholder="Optional" className="mt-1" data-testid="sm-awb-number" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Dispatch Date</Label>
              <Input type="date" value={selfManaged.dispatch_date}
                onChange={e => setSelfManaged(p => ({ ...p, dispatch_date: e.target.value }))}
                className="mt-1" data-testid="sm-dispatch-date" />
            </div>
            <div>
              <Label className="text-sm">Shipping Cost (₹)</Label>
              <Input type="number" min="0" step="0.01" value={selfManaged.shipping_cost}
                onChange={e => setSelfManaged(p => ({ ...p, shipping_cost: e.target.value }))}
                placeholder="Optional" className="mt-1" data-testid="sm-shipping-cost" />
            </div>
          </div>
          <div>
            <Label className="text-sm">Tracking URL</Label>
            <Input value={selfManaged.tracking_url}
              onChange={e => setSelfManaged(p => ({ ...p, tracking_url: e.target.value }))}
              placeholder="https://... (optional)" className="mt-1" data-testid="sm-tracking-url" />
          </div>
          <div>
            <Label className="text-sm">Notes</Label>
            <Input value={selfManaged.notes}
              onChange={e => setSelfManaged(p => ({ ...p, notes: e.target.value }))}
              placeholder="Optional" className="mt-1" data-testid="sm-notes" />
          </div>
        </div>
        <Button className="w-full h-10" onClick={markDispatched} disabled={smCreating}
          data-testid="mark-dispatched-btn">
          {smCreating
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Marking…</>
            : <><CheckCircle2 className="h-4 w-4 mr-2" />Mark as Dispatched</>}
        </Button>
      </div>
    );
  }

  // ─── MAIN RENDER ──────────────────────────────────────────────────────────────

  const STEPS = ['Shipment Details', 'Shipping Options', 'Confirm'];

  return (
    <Dialog open={open} onOpenChange={v => !v && handleClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-5" data-testid="shipnow-modal" aria-describedby={undefined}>
        <DialogHeader className="pb-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Truck className="h-4 w-4" /> Ship Now
          </DialogTitle>
        </DialogHeader>

        {/* ── Tab bar ──────────────────────────────────────────────────────────── */}
        <div className="flex gap-0 rounded-lg bg-gray-100 p-1" data-testid="shipnow-tabs">
          {[
            { key: 'marketplace', label: 'Marketplace Shipping', icon: Truck },
            { key: 'self_managed', label: 'Self Managed',         icon: User  },
          ].map(({ key, label, icon: Icon }) => (
            <button key={key} type="button" onClick={() => setActiveTab(key)}
              data-testid={`tab-${key}`}
              className={[
                'flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-all',
                activeTab === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
              ].join(' ')}
            >
              <Icon className="h-3.5 w-3.5" />{label}
            </button>
          ))}
        </div>

        {/* ── Stepper (Marketplace, steps 1–3) ─────────────────────────────────── */}
        {activeTab === 'marketplace' && mpStep < 4 && (
          <Stepper currentStep={mpStep} steps={STEPS} />
        )}

        {/* ── Content ──────────────────────────────────────────────────────────── */}
        {activeTab === 'marketplace' && mpStep === 1 && renderMpStep1()}
        {activeTab === 'marketplace' && mpStep === 2 && renderMpStep2()}
        {activeTab === 'marketplace' && mpStep === 3 && renderMpStep3()}
        {activeTab === 'marketplace' && mpStep === 4 && renderMpStep4()}
        {activeTab === 'self_managed' && renderSelfManaged()}
      </DialogContent>
    </Dialog>
  );
}
